/** Parse ScriptComponent / quote-formatted price (e.g. en-IN commas) to a finite number for API payloads. */
export function parsePriceToNumber(raw) {
  if (raw == null || raw === "") return 0;
  const s = String(raw).replace(/,/g, "").trim();
  if (s === "") return 0;
  const n = Number(s);
  return Number.isFinite(n) ? n : 0;
}

export function fmtQuoteValue(v) {
  if (v == null || v === "") return "—";
  const n = Number(v);
  if (Number.isNaN(n) || !Number.isFinite(n)) return String(v);
  if (Number.isInteger(n) && Math.abs(n) >= 1000) {
    return n.toLocaleString("en-IN");
  }
  if (!Number.isInteger(n)) {
    return n.toLocaleString("en-IN", { maximumFractionDigits: 2, minimumFractionDigits: 0 });
  }
  return String(n);
}

/** Circuit bound for limit-price clamping: null means skip that side (missing, non-finite, or zero). */
export function parseCircuitLimitBound(raw) {
  if (raw == null || raw === "") return null;
  const n = Number(String(raw).replace(/,/g, "").trim());
  if (!Number.isFinite(n) || n === 0) return null;
  return n;
}

export const LIMIT_ORDER_UI_LABEL = "Limit Order";

export function isLimitOrderUiType(orderType) {
  return String(orderType ?? "").trim() === LIMIT_ORDER_UI_LABEL;
}

/** Circuit price cap applies only for NSE/BSE cash + Limit Order when the quote has ckt bounds. */
export function shouldApplyCircuitPriceLimit(exchange, orderType, lowerCircuit, upperCircuit) {
  if (!isCashEquityExchange(exchange)) return false;
  if (!isLimitOrderUiType(orderType)) return false;
  return lowerCircuit != null || upperCircuit != null;
}

/** User-facing reason for the Limit price Low ckt / Up ckt cap. */
export function circuitPriceLimitReason(lowerCircuit, upperCircuit, hit) {
  const low = lowerCircuit != null ? fmtQuoteValue(lowerCircuit) : null;
  const high = upperCircuit != null ? fmtQuoteValue(upperCircuit) : null;
  if (hit === "high" && high) {
    return `Price cannot be above Up ckt (${high}).`;
  }
  if (hit === "low" && low) {
    return `Price cannot be below Low ckt (${low}).`;
  }
  if (low && high) {
    return `Limit price must be between Low ckt (${low}) and Up ckt (${high}).`;
  }
  if (low) return `Limit price cannot be below Low ckt (${low}).`;
  if (high) return `Limit price cannot be above Up ckt (${high}).`;
  return "";
}

export function circuitBoundHit(priceStr, lowerCircuit, upperCircuit) {
  const n = parsePriceToNumber(priceStr);
  if (!Number.isFinite(n)) return null;
  let minB = lowerCircuit == null ? null : lowerCircuit;
  let maxB = upperCircuit == null ? null : upperCircuit;
  if (minB != null && maxB != null && minB > maxB) {
    const tmp = minB;
    minB = maxB;
    maxB = tmp;
  }
  if (maxB != null && n > maxB) return "high";
  if (minB != null && n < minB) return "low";
  return null;
}

/** Informational only — Limit Order only; never changes the entered price. */
export function priceVsLtpWarning(priceStr, ltpRaw, transactionType, orderType) {
  if (!isLimitOrderUiType(orderType)) return "";
  const trimmed = String(priceStr ?? "").trim();
  if (trimmed === "" || /[.,]$/.test(trimmed)) return "";
  const price = parsePriceToNumber(trimmed);
  const ltp = parsePriceToNumber(ltpRaw);
  if (!Number.isFinite(price) || price <= 0) return "";
  if (!Number.isFinite(ltp) || ltp <= 0) return "";
  const side = String(transactionType ?? "").trim().toLowerCase();
  const ltpLabel = fmtQuoteValue(ltp);
  if (side === "buy" && price > ltp) {
    return `You're buying above LTP (${ltpLabel}).`;
  }
  if (side === "sell" && price < ltp) {
    return `You're selling below LTP (${ltpLabel}).`;
  }
  return "";
}

/**
 * Limit Order on NSE/BSE: clamp price to Low ckt … Up ckt (inclusive).
 * Other exchanges / order types are unchanged.
 */
export function clampPriceToCircuitLimits(priceStr, orderType, lowerCircuit, upperCircuit, exchange) {
  if (!shouldApplyCircuitPriceLimit(exchange, orderType, lowerCircuit, upperCircuit)) {
    return priceStr;
  }
  if (priceStr == null) return priceStr;
  const s = String(priceStr).trim();
  if (s === "") return "";

  const n = parsePriceToNumber(s);
  let minB = lowerCircuit == null ? null : lowerCircuit;
  let maxB = upperCircuit == null ? null : upperCircuit;
  if (minB != null && maxB != null && minB > maxB) {
    const tmp = minB;
    minB = maxB;
    maxB = tmp;
  }

  let x = n;
  if (minB != null) x = Math.max(minB, x);
  if (maxB != null) x = Math.min(maxB, x);
  if (x === n) return priceStr;
  return fmtQuoteValue(x);
}

/**
 * While typing a Limit price on NSE/BSE: never allow a value above Up ckt.
 * Values below Low ckt are allowed until blur (so "1" can become "105").
 */
export function constrainCircuitPriceInput(priceStr, orderType, lowerCircuit, upperCircuit, exchange) {
  if (!shouldApplyCircuitPriceLimit(exchange, orderType, lowerCircuit, upperCircuit)) {
    return priceStr;
  }
  if (priceStr == null) return priceStr;
  const s = String(priceStr);
  const trimmed = s.trim();
  if (trimmed === "" || /[.,]$/.test(trimmed)) return s;

  const n = parsePriceToNumber(s);
  if (!Number.isFinite(n)) return s;

  let maxB = upperCircuit == null ? null : upperCircuit;
  let minB = lowerCircuit == null ? null : lowerCircuit;
  if (minB != null && maxB != null && minB > maxB) {
    const tmp = minB;
    minB = maxB;
    maxB = tmp;
  }
  if (maxB != null && n > maxB) return fmtQuoteValue(maxB);
  return s;
}

export function clampNumericPriceToCircuit(priceNum, orderType, lowerCircuit, upperCircuit, exchange) {
  if (!shouldApplyCircuitPriceLimit(exchange, orderType, lowerCircuit, upperCircuit)) {
    return priceNum;
  }
  const clamped = clampPriceToCircuitLimits(
    String(priceNum ?? ""),
    orderType,
    lowerCircuit,
    upperCircuit,
    exchange
  );
  return parsePriceToNumber(clamped);
}

export function mapQuoteDataToScriptProps(quoteItem, selectionExchange) {
  if (!quoteItem) return {};
  const {
    depth,
    ohlc,
    last_price,
    pct_change,
    average_price,
    volume,
    underlying_last_price,
    lower_circuit_limit,
    upper_circuit_limit,
    oi,
    last_quantity,
    last_trade_time,
    buy_quantity,
    sell_quantity,
    tradingsymbol,
    lot_size,
    exchange: quoteExchange,
  } = quoteItem;
  const pctStr = pct_change != null ? `${pct_change >= 0 ? "+" : ""}${pct_change.toFixed(2)}%` : "—";
  const fmt = (v) => fmtQuoteValue(v);
  const cash = isCashEquityExchange(quoteExchange ?? selectionExchange);
  const ohlcAndCkt = [
    { value: fmt(ohlc?.open), label: "Open" },
    { value: fmt(ohlc?.high), label: "High" },
    { value: fmt(ohlc?.low), label: "Low" },
    { value: fmt(ohlc?.close), label: "Prev. Close" },
    { value: fmt(lower_circuit_limit), label: "Low ckt" },
    { value: fmt(upper_circuit_limit), label: "Up ckt" },
  ];
  const ltpCluster = [
    { value: fmt(last_price), label: "LTP", highlight: true },
    { value: fmt(last_quantity), label: "LT Qty", highlight: true },
    {
      value: pctStr,
      label: "% Chg",
      highlight: true,
      positive: pct_change != null && pct_change >= 0,
      negative: pct_change != null && pct_change < 0,
    },
  ];
  const volumeMetric = { value: fmt(volume), label: "Volume" };
  const atpMetric = { value: fmt(average_price), label: "ATP" };
  const quoteMetrics = cash
    ? [...ohlcAndCkt, volumeMetric, ...ltpCluster, atpMetric]
    : [
        ...ohlcAndCkt,
        { value: fmt(underlying_last_price), label: "Und LTP" },
        { value: fmt(oi), label: "OI" },
        volumeMetric,
        ...ltpCluster,
        atpMetric,
      ];
  return {
    headerSymbol: tradingsymbol ?? "",
    marginRequired: "",
    quoteMetrics,
    orderBookBuyRows: (depth?.buy ?? []).slice(0, 5).map((row) => ({
      order: row.orders != null && row.orders !== "" ? fmt(row.orders) : "—",
      quantity: fmt(row.quantity),
      price: row.price != null && row.price !== "" ? fmt(row.price) : "",
    })),
    orderBookSellRows: (depth?.sell ?? []).slice(0, 5).map((row) => ({
      order: row.orders != null && row.orders !== "" ? fmt(row.orders) : "—",
      quantity: fmt(row.quantity),
      price: row.price != null && row.price !== "" ? fmt(row.price) : "",
    })),
    bidFooter: [
      { label: "TBQ", value: fmt(buy_quantity) },
      { label: "LTT", value: last_trade_time ?? "—" },
    ],
    askFooter: [
      { label: "LSQ", value: fmt(sell_quantity) },
      { label: "LUT", value: last_trade_time ?? "—" },
    ],
    defaultPrice: last_price != null ? fmt(last_price) : "",
    defaultQtyLots: "",
    defaultAbsDisQty: "",
    quoteLotSize: lot_size != null && lot_size !== "" ? Number(lot_size) : undefined,
    lowerCircuitLimit: parseCircuitLimitBound(lower_circuit_limit),
    upperCircuitLimit: parseCircuitLimitBound(upper_circuit_limit),
  };
}

export function apiExpiryToDisplay(apiDate) {
  if (!apiDate || typeof apiDate !== "string") return "";
  const [y, m, d] = apiDate.split("-");
  return d && m && y ? `${d}-${m}-${y}` : apiDate;
}

export function displayExpiryToApi(display) {
  if (!display || typeof display !== "string") return "";
  const parts = display.trim().split("-");
  if (parts.length === 3 && parts[0].length <= 2 && parts[1].length <= 2) {
    const [d, m, y] = parts;
    return `${y}-${m}-${d}`;
  }
  return display;
}

export function formatAvailableMargin(val) {
  if (val == null || val === "") return "—";
  const n = Number(val);
  if (Number.isNaN(n)) return String(val);
  return n.toLocaleString("en-IN", { maximumFractionDigits: 0 });
}

const INFINITY_THRESHOLD = 1e10;

function normalizeInfinityToken(raw) {
  if (raw == null || raw === "") return null;
  if (typeof raw === "number") {
    if (raw === Infinity) return 1;
    if (raw === -Infinity) return -1;
    if (Number.isFinite(raw) && Math.abs(raw) > INFINITY_THRESHOLD) return raw > 0 ? 1 : -1;
    return null;
  }
  const s = String(raw).replace(/,/g, "").trim().toLowerCase();
  if (!s) return null;
  if (s === "inf" || s === "+inf" || s === "infinity" || s === "+infinity") return 1;
  if (s === "-inf" || s === "-infinity") return -1;
  const n = Number(s);
  if (n === Infinity) return 1;
  if (n === -Infinity) return -1;
  if (Number.isFinite(n) && Math.abs(n) > INFINITY_THRESHOLD) return n > 0 ? 1 : -1;
  return null;
}

export function isScripInfinite(val) {
  return normalizeInfinityToken(val) != null;
}

export function parseScripNumeric(val) {
  if (val == null || val === "") return NaN;
  const infSign = normalizeInfinityToken(val);
  if (infSign === 1) return Infinity;
  if (infSign === -1) return -Infinity;
  if (typeof val === "number") return val;
  return Number(String(val).replace(/,/g, "").trim());
}

export function formatScripValue(val) {
  if (val == null) return "—";
  if (isScripInfinite(val)) return "unlimited";
  const n = parseScripNumeric(val);
  if (Number.isNaN(n)) return String(val);
  return n.toLocaleString("en-IN", { maximumFractionDigits: 2 });
}

export function formatPctOfMarginRequired(amountRaw, marginRaw) {
  const margin = parseScripNumeric(marginRaw);
  if (!Number.isFinite(margin) || margin <= 0) return "—";

  // Infinite P/L → no % next to "unlimited"
  if (isScripInfinite(amountRaw)) return "—";

  const amount = parseScripNumeric(amountRaw);
  if (Number.isNaN(amount)) return "—";

  const pct = (amount / margin) * 100;
  const absFormatted = Math.abs(pct).toLocaleString("en-IN", {
    maximumFractionDigits: 2,
    minimumFractionDigits: 0,
  });
  const sign = pct >= 0 ? "+" : "-";
  return `${sign}${absFormatted}%`;
}

/** Reward/Risk = |Max Profit| / |Max Loss|. */
export function formatRewardRisk(maxProfitRaw, maxLossRaw) {
  if (maxProfitRaw == null || maxLossRaw == null || maxProfitRaw === "" || maxLossRaw === "") {
    return "—";
  }
  const profitInf = normalizeInfinityToken(maxProfitRaw);
  const lossInf = normalizeInfinityToken(maxLossRaw);
  if (profitInf != null && lossInf != null) return "—";
  if (profitInf != null) return "—";
  if (lossInf != null) return "0";

  const profit = Math.abs(parseScripNumeric(maxProfitRaw));
  const loss = Math.abs(parseScripNumeric(maxLossRaw));
  if (!Number.isFinite(profit) || !Number.isFinite(loss) || loss === 0) return "—";

  const ratio = profit / loss;
  return ratio.toLocaleString("en-IN", { maximumFractionDigits: 2, minimumFractionDigits: 0 });
}

export const SCRIPT_FIELD_TO_API = {
  expiry: "expiry_date",
  scripType: "segment",
  tradingSymbol: "symbol",
  category: "option_type",
  strike: "strike_price",
};

export function getApiFieldForScriptSelection(field) {
  return SCRIPT_FIELD_TO_API[field] ?? field;
}

const SCRIP_DIMENSION_API_FIELDS = new Set([
  "exchange",
  "segment",
  "symbol",
  "option_type",
  "strike_price",
  "expiry_date",
]);

export function isScripDimensionApiField(apiField) {
  return SCRIP_DIMENSION_API_FIELDS.has(apiField);
}

/**
 * Exact cascade from legacy TriggeredBy JS:
 * - Exchange → Segment, Symbol, OptionType, StrikePrice, ExpiryDate = ""
 * - Segment  → OptionType, StrikePrice, ExpiryDate = ""  (Symbol kept)
 * - Symbol   → OptionType, StrikePrice, ExpiryDate = ""
 */
export function applyScripDimensionCascade(selection, apiField, apiValue) {
  const s = selection || {};
  if (apiField === "exchange") {
    const prevEx = (s.exchange ?? "").toString().trim();
    const nextEx = (apiValue ?? "").toString().trim();
    if (prevEx === nextEx) return s;
    return {
      ...s,
      exchange: apiValue,
      segment: "",
      symbol: "",
      option_type: "",
      strike_price: "",
      expiry_date: "",
    };
  }
  if (apiField === "segment") {
    const prevSeg = (s.segment ?? "").toString().trim();
    const nextSeg = (apiValue ?? "").toString().trim();
    if (prevSeg === nextSeg) return s;
    const omitSymbol = shouldOmitMcxTradingSymbol(s.exchange, apiValue);
    // Filling first option after an empty API request — don't clear again
    if (!prevSeg) {
      return omitSymbol ? { ...s, segment: apiValue, symbol: "" } : { ...s, segment: apiValue };
    }
    return {
      ...s,
      segment: apiValue,
      symbol: omitSymbol ? "" : s.symbol,
      option_type: "",
      strike_price: "",
      expiry_date: "",
    };
  }
  if (apiField === "symbol") {
    const prevSym = (s.symbol ?? "").toString().trim();
    const nextSym = (apiValue ?? "").toString().trim();
    if (prevSym === nextSym) return s;
    if (!prevSym) return { ...s, symbol: apiValue };
    return {
      ...s,
      symbol: apiValue,
      option_type: "",
      strike_price: "",
      expiry_date: "",
    };
  }
  return { ...s, [apiField]: apiValue };
}

/** Qty/Lots: integer, minimum 1. Empty or invalid uses fallback (also floored, min 1). */
export function clampQtyLots(raw, fallbackLotSize = 1) {
  const fbRaw = Number(fallbackLotSize);
  const fb = Number.isFinite(fbRaw) ? Math.max(1, Math.floor(fbRaw)) : 1;
  const t = raw != null ? String(raw).trim() : "";
  if (t === "") return fb;
  const n = Number(t);
  if (!Number.isFinite(n)) return fb;
  return Math.max(1, Math.floor(n));
}

/**
 * Qty/Lots field is treated as lots when quote API lot_size > 1.
 * Otherwise the field is share quantity (no lot multiply).
 */
export function usesLotSizeQuantity(lotSize) {
  const ls = Number(lotSize);
  return Number.isFinite(ls) && ls > 1;
}

/** NSE / BSE cash equity (not F&O). Used for strike/expiry/order-type UI — not for lot math. */
export function isCashEquityExchange(exchange) {
  const ex = String(exchange ?? "").trim().toUpperCase();
  return ex === "NSE" || ex === "BSE";
}

/** F&O / commodity / currency exchanges that use expiry (NFO, BFO, MCX, MXC, …). */
const FNO_EXCHANGES = new Set(["NFO", "BFO", "CDS", "BCD", "MCX", "MXC", "NCDEX"]);

export function isFnoExchange(exchange) {
  return FNO_EXCHANGES.has(String(exchange ?? "").trim().toUpperCase());
}

export function isMcxExchange(exchange) {
  const ex = String(exchange ?? "").trim().toUpperCase();
  return ex === "MCX" || ex === "MXC";
}

export function isIndicesSegment(segment) {
  const seg = String(segment ?? "").trim().toUpperCase();
  return seg.includes("INDICE") || seg.includes("INDEX");
}

/** MCX non-Indices: dropdown-options API must not send trading symbol. */
export function shouldOmitMcxTradingSymbol(exchange, segment) {
  if (!isMcxExchange(exchange)) return false;
  const seg = String(segment ?? "").trim();
  if (!seg) return false;
  return !isIndicesSegment(seg);
}

/** Non-cash exchanges (NFO, BFO, MXC, …). Used for product-type UI. */
export function isDerivativesExchange(exchange) {
  const ex = String(exchange ?? "").trim().toUpperCase();
  if (!ex) return false;
  return !isCashEquityExchange(ex);
}

/** Futures segment (e.g. NFO-FUT, BFO-FUT, MCX-FUT). */
export function isFuturesSegment(segment) {
  return String(segment ?? "").trim().toUpperCase().includes("FUT");
}

/** Options segment (e.g. NFO-OPT, BFO-OPT). */
export function isOptionsSegment(segment) {
  return String(segment ?? "").trim().toUpperCase().includes("OPT");
}

/** Strike is editable only for options (NFO-OPT / BFO-OPT). Cash and FUT stay locked. */
export function isStrikeEditable(exchange, segment) {
  if (isCashEquityExchange(exchange)) return false;
  return isOptionsSegment(segment);
}

/**
 * Expiry is required for FUT/OPT segments only.
 * Other non-cash exchanges (e.g. MXC without FUT/OPT in segment) quote with
 * exchange + segment + symbol — same as cash — so get_quote_data is not blocked.
 */
export function isExpiryEditable(exchange, segment) {
  if (isCashEquityExchange(exchange)) return false;
  const seg = String(segment ?? "").trim();
  if (isOptionsSegment(seg) || isFuturesSegment(seg)) return true;
  if (!seg && isFnoExchange(exchange)) return true;
  return false;
}

/** Category is editable on FUT/OPT. Cash and other segments stay locked. */
export function isCategoryEditable(exchange, segment) {
  if (isCashEquityExchange(exchange)) return false;
  if (!segment) return isFnoExchange(exchange);
  return isOptionsSegment(segment) || isFuturesSegment(segment);
}

/** Disabled Category is always sent as "" — never a leftover CE/PE. */
export function optionTypeForApi(sel) {
  if (!isCategoryEditable(sel?.exchange, sel?.segment)) return "";
  return String(sel?.option_type ?? "").trim();
}

/** Disabled Strike is always sent as "" — never 0 or a leftover strike. */
export function strikePriceForApi(sel) {
  if (!isStrikeEditable(sel?.exchange, sel?.segment)) return "";
  const raw = sel?.strike_price;
  if (raw === "" || raw == null) return "";
  return raw;
}

/** True when a row has enough fields to call get_quote_data. */
export function isQuoteSelectionComplete(sel) {
  if (!sel) return false;
  const exchange = String(sel.exchange ?? "").trim();
  const segment = String(sel.segment ?? "").trim();
  const symbol = String(sel.symbol ?? "").trim();
  if (!exchange || !segment || !symbol) return false;

  if (isOptionsSegment(segment)) {
    const optionType = String(sel.option_type ?? "").trim();
    const expiry = String(sel.expiry_date ?? "").trim();
    const strikeRaw = sel.strike_price;
    const hasStrike = strikeRaw !== "" && strikeRaw != null && !Number.isNaN(Number(strikeRaw));
    return Boolean(optionType && expiry && hasStrike);
  }
  if (isExpiryEditable(exchange, segment)) {
    return Boolean(String(sel.expiry_date ?? "").trim());
  }
  // Cash equity and other exchanges (e.g. MXC): exchange + segment + symbol is enough
  return true;
}

/** Abs disclosed qty: lots × lot_size when lot_size > 1; else ~10% of qty. */
export function deriveAbsDisclosedQty(qtyLotsStr, lotSize) {
  const t = qtyLotsStr != null ? String(qtyLotsStr).trim() : "";
  if (t === "") return "";
  const base = clampQtyLots(t, 1);
  if (usesLotSizeQuantity(lotSize)) {
    const ls = Number(lotSize);
    return String(Math.max(0, Math.round(base * ls)));
  }
  return String(Math.max(0, Math.round(base * 0.1)));
}

/** Scrip-info input_quantity: raw lots when lot_size > 1; else clamped share qty. */
export function scripInfoInputQuantity(qtyLotsStr, lotSize) {
  if (usesLotSizeQuantity(lotSize)) {
    return qtyLotsStr != null ? String(qtyLotsStr).trim() : "";
  }
  return String(clampQtyLots(qtyLotsStr, 1));
}

/** Order qty: lots × lot_size when lot_size > 1; else the entered qty. */
export function orderQuantityFromQtyLotsField(qtyLotsStr, lotSize) {
  const lotsOrQty = clampQtyLots(qtyLotsStr, 1);
  if (usesLotSizeQuantity(lotSize)) {
    const ls = Number(lotSize);
    return Math.max(1, Math.round(lotsOrQty * ls));
  }
  return lotsOrQty;
}

/** Breakeven% vs underlying: ((point - underlying) / underlying) * 100. */
export function formatBreakevenWithPct(breakevenList, underlyingPriceRaw) {
  if (!Array.isArray(breakevenList) || breakevenList.length === 0) return "—";
  const underlying = parseScripNumeric(underlyingPriceRaw);
  const hasUnderlying = Number.isFinite(underlying) && underlying !== 0;

  return breakevenList
    .filter((v) => v != null && v !== "")
    .map((point) => {
      const priceStr = formatScripValue(point);
      if (!hasUnderlying || isScripInfinite(point)) return priceStr;
      const n = parseScripNumeric(point);
      if (!Number.isFinite(n)) return priceStr;
      const pct = ((n - underlying) / underlying) * 100;
      const absFormatted = Math.abs(pct).toLocaleString("en-IN", {
        maximumFractionDigits: 2,
        minimumFractionDigits: 0,
      });
      const sign = pct >= 0 ? "" : "-";
      // Match example: 22985.65 (-5.99%), 25514.35 (4.36%)
      return `${priceStr} (${sign}${absFormatted}%)`;
    })
    .join(", ");
}

export function mapOrderInitiationScripInfoResponse(data) {
  if (!data || (data.status !== true && data.Status !== true)) return null;
  const strategy = data.strategy_builder_info || data.StrategyInfo || {};
  const margin = data.margin_info || data.MarginInfo || {};

  const rawList = strategy.breakeven_list ?? strategy.BreakevenList;
  let breakevenList = Array.isArray(rawList) ? rawList.filter((v) => v != null && v !== "") : [];
  // API may send breakeven_point as a number or as an array of points
  if (breakevenList.length === 0) {
    const point = strategy.breakeven_point ?? strategy.BreakevenPoint ?? strategy.breakeven;
    if (Array.isArray(point)) {
      breakevenList = point.filter((v) => v != null && v !== "");
    } else if (point != null && point !== "") {
      breakevenList = [point];
    }
  }

  return {
    Status: data.status === true || data.Status === true,
    StrategyInfo: {
      MaxProfit: strategy.max_profit ?? strategy.MaxProfit,
      MaxLoss: strategy.max_loss ?? strategy.MaxLoss,
      BreakevenList: breakevenList,
      UnderlyingPrice: strategy.underlying_price ?? strategy.UnderlyingPrice,
      TotalPremium: strategy.total_premium ?? strategy.TotalPremium,
      PremiumPerLot: strategy.premium_per_lot ?? strategy.PremiumPerLot,
      RewardRisk: strategy.reward_risk ?? strategy.RewardRisk ?? strategy.profit_probability ?? "—",
    },
    MarginInfo: {
      InitialMarginRequired: margin.initial_margin_required ?? margin.InitialMarginRequired,
    },
  };
}

export const EMPTY_SCRIPT_SELECTION = {
  exchange: "",
  segment: "",
  symbol: "",
  option_type: "",
  strike_price: "",
  expiry_date: "",
  transactionType: "Buy",
  qtyLots: "1",
  absDisQty: "",
  productType: "",
  orderType: "",
  price: "",
  validity: "",
  amType: "",
  amPrice: "",
  sweeping: false,
  autoModify: false,
  strategy: "",
};
